import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useT } from "@/i18n/LanguageProvider";

/* Cartographie abstraite de l'exposition numérique — composée en CSS/SVG,
   sans image externe. Nœuds = sources d'information ; le point rouge
   signale un élément indexé / publiquement exposé (doublé d'un libellé
   texte pour ne pas reposer sur la couleur seule). */
const NODES = [
  { id: "registre", label: "Registre officiel", meta: "Source", x: 10, y: 8, exposed: false },
  { id: "annuaire", label: "Annuaire en ligne", meta: "Indexé", x: 66, y: 4, exposed: true },
  { id: "presse", label: "Article ancien", meta: "Source", x: 2, y: 56, exposed: false },
  { id: "pdf", label: "Document PDF", meta: "Indexé", x: 72, y: 46, exposed: true },
  { id: "moteur", label: "Moteur de recherche", meta: "Indexé", x: 30, y: 82, exposed: true },
];

const CENTER = { x: 38, y: 36 };

function Cartography() {
  return (
    <div data-testid="hero-cartography" aria-hidden="true" className="select-none animate-fade-in">
      <div className="relative w-full h-[400px] xl:h-[430px]">
      {/* Trame de fond */}
      <div className="absolute inset-0 index-lines rounded-lg" />

      {/* Lignes de relation */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {NODES.map((n) => (
          <line
            key={n.id}
            x1={CENTER.x + 11}
            y1={CENTER.y + 8}
            x2={n.x + 11}
            y2={n.y + 8}
            stroke={n.exposed ? "#E94B3C" : "#C9C4BE"}
            strokeOpacity={n.exposed ? 0.4 : 0.9}
            strokeWidth="0.18"
            vectorEffect="non-scaling-stroke"
            style={{ strokeWidth: "1px" }}
          />
        ))}
      </svg>

      {/* Nœud central — identité */}
      <div
        className="absolute bg-[#181A1D] text-[#FAF9F7] rounded-[10px] px-5 py-4 shadow-[0_2px_12px_rgba(24,26,29,0.12)]"
        style={{ left: `${CENTER.x}%`, top: `${CENTER.y}%` }}
      >
        <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#B4B8BD] mb-1">
          Recherche nominative
        </p>
        <p className="text-[15px] font-semibold tracking-tight whitespace-nowrap">
          Votre identité
        </p>
      </div>

      {/* Nœuds satellites */}
      {NODES.map((n) => (
        <div
          key={n.id}
          className="absolute bg-white border border-[#E2DEDA] rounded-lg px-4 py-3"
          style={{ left: `${n.x}%`, top: `${n.y}%` }}
        >
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-[7px] h-[7px] rounded-full flex-shrink-0 ${
                n.exposed ? "bg-[#E94B3C]" : "bg-[#B4AFA8]"
              }`}
            />
            <p className="font-mono text-[10px] tracking-[0.12em] uppercase text-[#7C8086] whitespace-nowrap">
              {n.meta}
            </p>
          </div>
          <p className="mt-1 text-[13px] font-medium text-[#181A1D] tracking-tight whitespace-nowrap">
            {n.label}
          </p>
        </div>
      ))}

      </div>

      {/* Légende — élément d'explication distinct, respiration nette */}
      <div className="mt-8 pt-4 border-t border-[#EFEBE6] flex flex-wrap items-center gap-x-5 gap-y-1">
        <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[#5A5E64]">
          <span className="inline-block w-[7px] h-[7px] rounded-full bg-[#E94B3C]" />
          Exposé publiquement
        </span>
        <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.1em] uppercase text-[#7C8086]">
          <span className="inline-block w-[7px] h-[7px] rounded-full bg-[#B4AFA8]" />
          Source d'origine
        </span>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const { t } = useT();
  const navigate = useNavigate();
  const reviewPath = "/audit-initial";

  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="relative overflow-hidden bg-[#FAF9F7] grain"
    >
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 lg:pt-44 pb-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          {/* Colonne gauche — positionnement */}
          <div className="lg:col-span-6 max-w-2xl">
            <h1
              data-testid="hero-title"
              className="font-sans font-semibold text-[#181A1D] text-balance tracking-tight leading-[1.06] text-4xl md:text-5xl lg:text-[58px] animate-fade-up"
              style={{ animationDelay: "60ms" }}
            >
              {t("hero.titlePre")}
              <em className="not-italic">{t("hero.titleEm")}</em>
              {t("hero.titlePost")}
            </h1>

            <div
              aria-hidden="true"
              data-testid="h1-marker"
              className="h1-marker mt-8 animate-fade-up"
              style={{ animationDelay: "90ms" }}
            />

            <p
              data-testid="hero-description"
              className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-[#52565C] text-pretty animate-fade-up"
              style={{ animationDelay: "120ms" }}
            >
              {t("hero.description")}
            </p>

            <div
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 animate-fade-up"
              style={{ animationDelay: "180ms" }}
            >
              <a
                href={reviewPath}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(reviewPath);
                }}
                data-testid="hero-cta-diagnostic"
                className="btn-primary"
              >
                {t("common.ctaDiagnostic")}
                <ArrowRight size={14} strokeWidth={1.5} />
              </a>
              <a
                href="#methode-resume"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("methode-resume");
                  if (el) {
                    const y = el.getBoundingClientRect().top + window.scrollY - 64;
                    window.scrollTo({ top: y, behavior: "smooth" });
                  }
                }}
                data-testid="hero-cta-methode"
                className="text-[14px] text-[#5A5E64] hover:text-[#181A1D] underline decoration-[#D8D3CD] underline-offset-4 hover:decoration-[#E94B3C] transition-colors duration-200"
              >
                {t("common.ctaApproach")}
              </a>
            </div>

            <p
              data-testid="hero-disclaimer"
              className="mt-12 max-w-xl text-[13px] leading-relaxed text-[#7C8086] animate-fade-up"
              style={{ animationDelay: "260ms" }}
            >
              {t("hero.disclaimer")}
            </p>
          </div>

          {/* Colonne droite — cartographie de l'exposition */}
          <div className="hidden lg:block lg:col-span-6">
            <Cartography />
          </div>
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="h-px bg-[#E2DEDA]" />
      </div>
    </section>
  );
}
